import {
  Avatar,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOnOptionSelectData,
  TagPickerOption,
  TagPickerProps,
  Text,
} from "@fluentui/react-components";
import { VirtualizerScrollView } from "@fluentui-contrib/react-virtualizer";
import { getUsers } from "api";
import { IUser } from "models/IUser";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConstantValues } from "../../../common/constants";
import { mapAzureUserToModel } from "../../../utils/generalUtils";
import { usePeoplePickerStyles } from "./PeoplePicker.styles";
import { CustomOption, PeoplePickerProps } from "./PeoplePicker.types";

const NO_MATCHES_ITEM = "no-matches";

export const PeoplePicker = (props: PeoplePickerProps): JSX.Element => {
  const styles = usePeoplePickerStyles();

  const [matchingOptions, setMatchingOptions] = useState<CustomOption[] | null>(
    [],
  );
  const [isLoadingResources, setIsLoadingResources] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const refInput = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const onOptionSelect: TagPickerProps["onOptionSelect"] = (
    event: Event | SyntheticEvent<Element, Event>,
    data: TagPickerOnOptionSelectData,
  ) => {
    if (data?.value === NO_MATCHES_ITEM) {
      return;
    }

    const remainingOptionsAfterSelectedRemoval: CustomOption[] =
      props.selectedOptions?.filter(
        (o: CustomOption): boolean => o?.key !== data?.value,
      ) ?? [];
    if (
      remainingOptionsAfterSelectedRemoval.length !==
      (props?.selectedOptions?.length ?? 0)
    ) {
      props.handleSelectedItems(remainingOptionsAfterSelectedRemoval);
    } else {
      const matchedOption: CustomOption[] | undefined = matchingOptions?.filter(
        (m: CustomOption) => m?.key === data?.value,
      );
      const newlySelectedOptions: CustomOption[] = [
        ...(props.selectedOptions || []),
        ...(matchedOption || []),
      ];
      props.handleSelectedItems(newlySelectedOptions);
    }

    setInputValue(ConstantValues.EMPTY_STRING);
  };

  const fetchUsers = useCallback(
    async (value: string): Promise<void> => {
      setIsLoadingResources(true);
      try {
        const usersFromText: IUser[] = await getUsers({
          text: value,
          hasOnlyOurUsers: props.hasOnlyOurUsers ?? false,
        } as { text: string; hasOnlyOurUsers?: boolean });

        const selectedIds =
          props.selectedOptions
            ?.map((o) => o.id)
            .filter((id): id is string => id !== undefined) ?? [];

        const matches = usersFromText
          ?.map((user: IUser) => mapAzureUserToModel(user))
          ?.filter(
            (option: CustomOption) =>
              !option.id || !selectedIds.includes(option.id),
          )
          ?.sort((a: CustomOption, b: CustomOption) => {
            const lastA = a.text.split(" ").slice(1).join(" ") || a.text;
            const lastB = b.text.split(" ").slice(1).join(" ") || b.text;
            const firstA = a.text.split(" ")[0];
            const firstB = b.text.split(" ")[0];
            return firstA.localeCompare(firstB) || lastA.localeCompare(lastB);
          });

        setMatchingOptions(matches);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setMatchingOptions([]);
      } finally {
        setIsLoadingResources(false);
      }
    },
    [props.hasOnlyOurUsers, props.selectedOptions],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value: string = event.target.value;
    setInputValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchUsers(value);
    }, 300);
  };

  const getTagPickerOption = (option: CustomOption): JSX.Element => {
    return (
      <TagPickerOption
        key={option.key}
        value={option?.key}
        text={option?.text ?? ConstantValues.EMPTY_STRING}
        title={option?.text ?? ConstantValues.EMPTY_STRING}
        media={
          <Avatar
            aria-hidden
            name={option?.text ?? ConstantValues.EMPTY_STRING}
            color="colorful"
          />
        }
      >
        <div className={styles.truncatedText}>
          <Text className={styles.optionText}>
            {option?.text ?? ConstantValues.EMPTY_STRING}
          </Text>
          <div className={styles.secondaryText}>
            {option?.secondaryText ?? ConstantValues.EMPTY_STRING}
          </div>
        </div>
      </TagPickerOption>
    );
  };

  const renderTagPickerListOptions = ():
    JSX.Element | JSX.Element[] | string => {
    if (isLoadingResources) {
      return <Spinner label={"Loading"} />;
    }

    if (inputValue && matchingOptions?.length === 0) {
      return (
        <TagPickerOption value={NO_MATCHES_ITEM} text="No matches found">
          {"No matches found"}
        </TagPickerOption>
      );
    }

    const unselectedOptions = matchingOptions?.filter(
      (option) =>
        !props.selectedOptions?.some((selected) => selected.key === option.key),
    );

    if (!unselectedOptions?.length) {
      return <></>;
    }

    return (
      <VirtualizerScrollView itemSize={50} numItems={unselectedOptions.length}>
        {(index: number) => getTagPickerOption(unselectedOptions[index])}
      </VirtualizerScrollView>
    );
  };

  const renderSelectedTagsPicker: JSX.Element[] | JSX.Element = useMemo(() => {
    if (!props?.selectedOptions) {
      return <></>;
    }

    return props?.selectedOptions?.map((selectedOption: CustomOption) => {
      return (
        <Tag
          value={selectedOption?.key}
          key={selectedOption?.key}
          disabled={props.disabled}
          title={selectedOption?.text ?? ConstantValues.EMPTY_STRING}
          primaryText={{ className: styles.truncatedText }}
          media={
            <Avatar
              aria-hidden
              name={selectedOption?.text ?? ConstantValues.EMPTY_STRING}
              color="colorful"
            />
          }
        >
          {selectedOption?.text ?? ConstantValues.EMPTY_STRING}
        </Tag>
      );
    });
  }, [props.selectedOptions, props.disabled, styles.truncatedText]);

  return (
    <TagPicker
      size="medium"
      disabled={props.disabled}
      onOptionSelect={onOptionSelect}
      selectedOptions={props?.selectedOptions?.map((o: CustomOption) => o?.key)}
      positioning={{ position: "below", pinned: true, strategy: "fixed" }}
    >
      <TagPickerControl className={styles.container}>
        <TagPickerGroup className={styles.tagGroup}>
          {renderSelectedTagsPicker}
        </TagPickerGroup>
        <TagPickerInput ref={refInput} value={inputValue} onChange={onChange} />
      </TagPickerControl>
      <TagPickerList className={styles.selectionContainer}>
        {renderTagPickerListOptions()}
      </TagPickerList>
    </TagPicker>
  );
};
